import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Mail, FileImage, Palette, Copy, Check, Wand2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from '../components/company/Logo';

interface LogoFormat {
  id: string;
  name: string;
  description: string;
  sizes: number[];
  usage: string;
  bgColor: string;
  textColor?: 'blue' | 'white' | 'black';
}

const logoFormats: LogoFormat[] = [
  {
    id: 'signature',
    name: 'Email Signature',
    description: 'Optimized for email signatures',
    sizes: [120, 150, 180],
    usage: 'Perfect for Gmail, Outlook, and other email clients',
    bgColor: 'transparent',
  },
  {
    id: 'light-bg',
    name: 'Light Background',
    description: 'Blue logo for light backgrounds',
    sizes: [200, 300, 400, 600],
    usage: 'Documents, presentations, websites',
    bgColor: 'white',
    textColor: 'blue',
  },
  {
    id: 'dark-bg',
    name: 'Dark Background',
    description: 'White logo for dark backgrounds',
    sizes: [200, 300, 400, 600],
    usage: 'Dark mode websites, presentations',
    bgColor: '#1e293b',
    textColor: 'white',
  },
  {
    id: 'social',
    name: 'Social Media',
    description: 'Square format for social profiles',
    sizes: [400, 800, 1200],
    usage: 'LinkedIn, Twitter, Facebook profiles',
    bgColor: 'white',
    textColor: 'blue',
  },
];

const PublicLogosPage = () => {
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const generateLogoDataUrl = (
    size: number,
    bgColor: string,
    textColor?: 'blue' | 'white' | 'black'
  ): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const scale = 2; // High DPI
      canvas.width = size * scale;
      canvas.height = size * scale;
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      ctx.scale(scale, scale);

      // Background
      if (bgColor !== 'transparent') {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, size, size);
      }

      // Logo elements
      const iconSize = size * 0.3;
      const iconX = size * 0.1;
      const iconY = size * 0.35;

      // Draw cube icon
      const color = textColor === 'white' ? '#ffffff' : '#2563eb';
      ctx.fillStyle = color;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;

      // Simple cube representation
      ctx.beginPath();
      ctx.moveTo(iconX + iconSize / 2, iconY);
      ctx.lineTo(iconX + iconSize, iconY + iconSize / 3);
      ctx.lineTo(iconX + iconSize, iconY + iconSize);
      ctx.lineTo(iconX + iconSize / 2, iconY + iconSize * 1.3);
      ctx.lineTo(iconX, iconY + iconSize);
      ctx.lineTo(iconX, iconY + iconSize / 3);
      ctx.closePath();
      ctx.fill();

      // Draw text
      const fontSize = size * 0.12;
      ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      ctx.fillStyle = color;
      ctx.fillText('ScaleNix', iconX + iconSize + size * 0.05, iconY + iconSize * 0.6);

      resolve(canvas.toDataURL('image/png'));
    });
  };

  const downloadLogo = async (
    formatId: string,
    size: number,
    bgColor: string,
    textColor?: 'blue' | 'white' | 'black'
  ) => {
    const dataUrl = await generateLogoDataUrl(size, bgColor, textColor);
    const link = document.createElement('a');
    link.download = `scalenix-logo-${formatId}-${size}px.png`;
    link.href = dataUrl;
    link.click();
  };

  const getLogoUrl = (formatId: string, size: number, bgColor: string, textColor?: string) => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const bg = bgColor === 'transparent' ? 'transparent' : encodeURIComponent(bgColor);
    const color = textColor || 'blue';
    return `${supabaseUrl}/functions/v1/logo-generator?format=${formatId}&size=${size}&bg=${bg}&color=${color}`;
  };

  const copyDirectUrl = (formatId: string, size: number, bgColor: string, textColor?: string) => {
    const url = getLogoUrl(formatId, size, bgColor, textColor);
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl mb-6 shadow-lg">
            <FileImage className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            ScaleNix Logos & Assets
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            Download our official logos in various formats and sizes for your signatures,
            documents, and presentations.
          </p>
          <Link
            to="/logo-generator"
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-lg"
          >
            <Wand2 className="h-5 w-5 mr-2" />
            Advanced Logo Generator
          </Link>
        </motion.div>

        {/* Preview Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Palette className="h-6 w-6 mr-2 text-blue-600" />
            Logo Preview
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Light background */}
            <div className="text-center">
              <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-3">
                <Logo size="lg" variant="blue" />
              </div>
              <p className="text-sm font-medium text-gray-700">Light Background</p>
            </div>

            {/* Dark background */}
            <div className="text-center">
              <div className="bg-slate-800 rounded-xl p-8 mb-3">
                <Logo size="lg" variant="white" />
              </div>
              <p className="text-sm font-medium text-gray-700">Dark Background</p>
            </div>

            {/* Transparent */}
            <div className="text-center">
              <div
                className="rounded-xl p-8 mb-3"
                style={{
                  backgroundImage:
                    'repeating-conic-gradient(#f3f4f6 0% 25%, white 0% 50%) 50% / 20px 20px',
                }}
              >
                <Logo size="lg" variant="blue" />
              </div>
              <p className="text-sm font-medium text-gray-700">Transparent</p>
            </div>
          </div>
        </motion.div>

        {/* Download Sections */}
        <div className="space-y-8">
          {logoFormats.map((format, index) => (
            <motion.div
              key={format.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">{format.name}</h3>
                    <p className="text-blue-100 mb-1">{format.description}</p>
                    <p className="text-sm text-blue-200 flex items-center">
                      <Mail className="h-4 w-4 mr-2" />
                      {format.usage}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-8">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {format.sizes.map((size) => (
                    <div
                      key={size}
                      className="border-2 border-gray-200 rounded-xl p-4 hover:border-blue-500 transition-colors"
                    >
                      <div className="text-center mb-4">
                        <div
                          className="mx-auto mb-3 rounded-lg overflow-hidden flex items-center justify-center"
                          style={{
                            width: '120px',
                            height: '120px',
                            backgroundColor:
                              format.bgColor === 'transparent'
                                ? 'white'
                                : format.bgColor,
                            backgroundImage:
                              format.bgColor === 'transparent'
                                ? 'repeating-conic-gradient(#f3f4f6 0% 25%, white 0% 50%) 50% / 20px 20px'
                                : undefined,
                          }}
                        >
                          <Logo
                            size="sm"
                            variant={format.textColor || 'blue'}
                          />
                        </div>
                        <p className="font-bold text-gray-900">{size}×{size}px</p>
                        <p className="text-xs text-gray-500">PNG Format</p>
                      </div>

                      <div className="space-y-2">
                        <button
                          onClick={() =>
                            downloadLogo(format.id, size, format.bgColor, format.textColor)
                          }
                          className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </button>

                        <button
                          onClick={() => copyDirectUrl(format.id, size, format.bgColor, format.textColor)}
                          className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                        >
                          {copiedUrl === getLogoUrl(format.id, size, format.bgColor, format.textColor) ? (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-2" />
                              Copy URL
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Direct URL Usage */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl shadow-lg p-8 mt-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <Copy className="h-6 w-6 mr-2 text-blue-600" />
            Direct URL Access
          </h2>
          <p className="text-gray-600 mb-4">
            Use these URLs directly in your email signatures, websites, or documents. The images are served dynamically as SVG for perfect quality at any size.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
            <p className="font-mono text-sm text-gray-700 mb-2">
              {import.meta.env.VITE_SUPABASE_URL}/functions/v1/logo-generator?size=<span className="text-blue-600">180</span>&bg=<span className="text-blue-600">transparent</span>&color=<span className="text-blue-600">blue</span>
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Parameters: <strong>size</strong> (120-1200px), <strong>bg</strong> (transparent, white, or hex color), <strong>color</strong> (blue, white, black)
            </p>
          </div>
        </motion.div>

        {/* Usage Guidelines */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl shadow-lg p-8 mt-8 border-2 border-blue-200"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Usage Guidelines</h2>
          <div className="grid md:grid-cols-2 gap-6 text-gray-700">
            <div>
              <h3 className="font-bold text-lg mb-3 text-blue-900">✓ Do's</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Use the logo in its original proportions</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Maintain adequate clear space around the logo</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Use appropriate versions for light/dark backgrounds</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Ensure minimum size for readability (120px width)</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-3 text-red-900">✗ Don'ts</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Don't stretch or distort the logo</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Don't change the logo colors</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Don't add effects, shadows, or outlines</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Don't rotate or flip the logo</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-12 text-gray-600"
        >
          <p>
            Need a different format or size?{' '}
            <a
              href="mailto:support@scalenix.fr"
              className="text-blue-600 hover:text-blue-700 font-medium underline"
            >
              Contact us
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PublicLogosPage;
